// SPDX-FileCopyrightText: 2025 Land Baden-Württemberg <InnoLab@stm.bwl.de>
//
// SPDX-License-Identifier: MPL-2.0

import keycloakService from './KeycloakService';

/**
 * GET
 * @param {string} path
 * @param {{ headers?: Record<string,string>, timeoutMs?: number, retries?: number }} [opts]
 */
export const get = (path, opts) => request('GET', path, undefined, opts);

/**
 * POST
 * @param {string} path
 * @param {any} body
 * @param {{ headers?: Record<string,string>, timeoutMs?: number, contentType?: string }} [opts]
 */
export const post = (path, body, opts) => request('POST', path, body, opts);

/**x
 * PUT
 * @param {string} path
 * @param {any} body
 * @param {{ headers?: Record<string,string>, timeoutMs?: number, contentType?: string }} [opts]
 */
export const put = (path, body, opts) => request('PUT', path, body, opts);

/**
 * DELETE
 * @param {string} path
 * @param {{ headers?: Record<string,string>, timeoutMs?: number }} [opts]
 */
export const del = (path, opts) => request('DELETE', path, undefined, opts);

/**
 * File-Upload (multipart/form-data)
 * @param {string} path
 * @param formData
 * @param {{ signal?: AbortSignal, onUploadProgress?: (progress: { loaded: number, total: number, percent: number }) => void, onUploadCompleted?: () => void }} [opts]
 */
export const upload = async (path, formData = {}, opts = {}) => {
  const token = keycloakService.getToken();
  const headers = buildHeaders(null, token);
  const { signal, onUploadProgress, onUploadCompleted } = opts;

  if (typeof onUploadProgress !== 'function') {
    const response = await fetch(path, {
      method: 'POST',
      headers,
      body: formData,
      signal,
    });

    if (response.status === 401 && token) {
      keycloakService.logout();
    }

    return response;
  }

  return uploadWithProgress(path, formData, headers, token, signal, onUploadProgress, onUploadCompleted);
};

// ===================== helper functions =====================

async function request(method, path, body, opts = {}) {
  const contentType = Object.prototype.hasOwnProperty.call(opts, 'contentType') ? opts.contentType : 'application/json';

  let currentToken = keycloakService.getToken();
  if (currentToken) {
    await keycloakService.updateToken(60);
    currentToken = keycloakService.getToken();
  }

  const send = async (token) => {
    const headers = buildHeaders(contentType, token, opts.headers);
    const sendJson = body != null && headers.get('Content-Type')?.includes('application/json');
    return fetch(path, {
      method,
      headers,
      body: body == null ? undefined : sendJson ? JSON.stringify(body) : body,
      signal: opts.signal,
    });
  };

  let response = await send(currentToken);

  if (response.status === 401 && currentToken) {
    const refreshed = await keycloakService.updateToken(0);
    if (refreshed) {
      currentToken = keycloakService.getToken();
      response = await send(currentToken);
    }
  }

  if (response.status === 401 && currentToken) {
    keycloakService.logout();
  }

  return response;
}

function buildHeaders(contentType, token = null, extraHeaders = null) {
  const headers = new Headers(extraHeaders ?? {});

  if (contentType) {
    headers.set('Content-Type', contentType);
  } else {
    headers.delete('Content-Type');
  }

  if (!headers.has('Accept')) {
    headers.set('Accept', 'application/json');
  }

  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  return headers;
}

function uploadWithProgress(path, formData, headers, token, signal, onUploadProgress, onUploadCompleted) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', path, true);

    for (const [name, value] of headers.entries()) {
      xhr.setRequestHeader(name, value);
    }

    const cleanupSignalListener = () => {
      if (signal) {
        signal.removeEventListener('abort', onAbortFromSignal);
      }
    };

    const onAbortFromSignal = () => {
      xhr.abort();
    };

    xhr.upload.onprogress = (event) => {
      const total = event.lengthComputable && event.total > 0 ? event.total : 0;
      const safeLoaded = Math.max(0, event.loaded);
      const percent = total > 0 ? Math.round((safeLoaded / total) * 100) : 0;
      onUploadProgress({
        loaded: safeLoaded,
        total,
        percent,
      });
    };

    xhr.upload.onload = () => {
      if (typeof onUploadCompleted === 'function') {
        onUploadCompleted();
      }
    };

    xhr.onload = () => {
      cleanupSignalListener();

      if (xhr.status === 401 && token) {
        keycloakService.logout();
      }

      const response = new Response(xhr.responseText, {
        status: xhr.status,
        statusText: xhr.statusText,
        headers: parseResponseHeaders(xhr.getAllResponseHeaders()),
      });

      resolve(response);
    };

    xhr.onerror = () => {
      cleanupSignalListener();
      reject(new TypeError('Network request failed'));
    };

    xhr.onabort = () => {
      cleanupSignalListener();
      reject(new DOMException('The operation was aborted.', 'AbortError'));
    };

    if (signal) {
      if (signal.aborted) {
        xhr.abort();
        return;
      }

      signal.addEventListener('abort', onAbortFromSignal, { once: true });
    }

    xhr.send(formData);
  });
}

function parseResponseHeaders(rawHeaders) {
  const headers = new Headers();
  if (!rawHeaders) {
    return headers;
  }

  rawHeaders
    .trim()
    .split(/[\r\n]+/)
    .forEach((line) => {
      const separatorIndex = line.indexOf(':');
      if (separatorIndex <= 0) {
        return;
      }
      const key = line.slice(0, separatorIndex).trim();
      const value = line.slice(separatorIndex + 1).trim();
      headers.append(key, value);
    });

  return headers;
}

export default {
  get,
  post,
  put,
  delete: del,
  upload,
};
